import { DateTime } from "luxon";
import {
    addMinutes,
    differenceInDays,
    differenceInMinutes,
    endOfDay,
    format, isValid,
    isWithinInterval,
    parse,
    set,
    startOfDay,
    startOfMinute,
    subMinutes,
    formatDistanceStrict
} from "date-fns";
import { intervalArrayFromState, intervalFromState } from "./IntervalHelper";
import { clampIntervals } from "../Common/Availability/Availability";

export const DateFormat = {
    // Alternative possibilities for Redux state dates
    //REDUX_STATE: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", // ISO 8601 equivalent
    //REDUX_STATE: "yyyy-MM-dd'T'HH:mm:ss.SSSSSSxxx", // ISO 8601 equivalent - currently used by server (3 extra digits for fraction of a second??)

    // Use this format for storing dates in redux state
    REDUX_STATE: "yyyy-MM-dd'T'HH:mm:ss",
    //REDUX_STATE: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", // ISO 8601 equivalent

    MYSQL_T: 'yyyy-MM-ddTHH:mm',
    MYSQL: 'yyyy-MM-dd HH:mm',
    MYSQL_DATE: 'yyyy-MM-dd',
    MYSQL_TIME: 'HH:mm',
    MYSQL_TIME_WITHOUT_SECONDS: 'HH:mm',
    HUMAN_SLASHED_DATE: 'dd/LL/yyyy',
    HUMAN_SLASHED_DATETIME: 'dd/LL/yyyy HH:mm',
    HUMAN_SLASHED_DATE_WITH_WEEKDAY: 'EEEE d LLLL',
    HUMAN_SLASHED_DATE_WITH_WEEKDAY_ABBREVIATED: 'EEE do LLL',
    WEEKDAY: 'EEEE',

    LOCALIZED_NUMERIC_DATE: 'D',
    LOCALIZED_DATE_ABBREVATED_MONTH: 'DD',
    LOCALIZED_DATE_FULL_MONTH: 'd MMMM y',

    // Luxon and date-fns have different date format codes because OF COURSE THEY DO
    LOCALIZED_NUMERIC_DATE_LUXON: 'D',
    LOCALIZED_DATE_ABBREVATED_MONTH_LUXON: 'DD',
    LOCALIZED_DATE_FULL_MONTH_LUXON: 'DDD',

    LOCALIZED_24HOUR_TIME: 'T',
};

export const DAYS_OF_THE_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const getDateTimeObject = (date, time) => {
    let updatedDate = DateTime.fromFormat(`${date} ${time}`, DateFormat.MYSQL)
    if (updatedDate) {
        return updatedDate.toISO({suppressMilliseconds: true});
    }
    return null;
}

/**
 * Returns just the date portion of an iso date string in a desired format
 * @param {string} isoDateString
 * @param {string} format
 * @returns {string}
 */
export const getDateFromDateString = (isoDateString, format = DateFormat.MYSQL_DATE) => {
    if (!isoDateString || !isoDateString.length) {
        return '';
    }
    let date = DateTime.fromISO(isoDateString);
    return date.toFormat(format);
}

export const getTimeFromDateString = (isoDateString, format = DateFormat.MYSQL_TIME) => {
    if (!isoDateString || !isoDateString.length) {
        return '';
    }
    let date = DateTime.fromISO(isoDateString);
    return date.toFormat(format);
}

/**
 * Always call this method to parse a date string stored in the redux state
 * @param {string} dateString
 * @returns {Date}
 */
export const dateFromState = (dateString) => {
    try {
        if (dateString instanceof Date) {
            return dateString;
        }

        if (!dateString || !dateString.length) {
            return null;
        }

        let date = null;
        date = parse(dateString, DateFormat.REDUX_STATE, getUTCDate());

        if (!isValid(date)) {
            date = parse(dateString, DateFormat.MYSQL_DATE, getUTCDate());
        }

        return date;
    } catch (e) {
        // Probably a knackered date string
    }

    return null;
}

export const getUTCDate = () => {
    // Get the current date and time in local timezone
    const currentDate = new Date();

    // Extract the date and time components
    const year = currentDate.getUTCFullYear();
    const month = currentDate.getUTCMonth();
    const day = currentDate.getUTCDate();
    const hours = currentDate.getUTCHours();
    const minutes = currentDate.getUTCMinutes();
    const seconds = currentDate.getUTCSeconds();
    const milliseconds = currentDate.getUTCMilliseconds();

    // Create a UTC date object using Date.UTC
    const utc = new Date(Date.UTC(year, month, day, hours, minutes, seconds, milliseconds));
    return utc;
}

/**
 * Always call this method to format a date string to be stored in redux state
 * @param {Date} date
 * @returns {string}
 */
export const dateToState = (date) => {
    try {
        if (!date) {
            return null;
        }
        // Already string encoded
        if (typeof date === 'string') {
            return date;
        }
        return format(date, DateFormat.REDUX_STATE);
    } catch (e) {
    }
}

/**
 * Get a datestring formatted for a date input from what we're storing in the state
 * @param dateString
 * @returns {string}
 */
export const dateForDateInputFromState = (dateString) => {
    if (!dateString) {
        return null;
    }

    dateString = ensureDateStringHasTime(dateString);

    let date = dateFromState(dateString)

    if (!date) {
        return null;
    }

    return format(date, DateFormat.MYSQL_DATE)
}

export const dateForStateFromDateInput = (inputDateString) => {
    let date = parse(inputDateString, DateFormat.MYSQL_DATE, new Date());

    // Just in case we're attempting to translate a string that is already in the correct format
    if (!isValid(date)) {
        date = parse(inputDateString, DateFormat.REDUX_STATE, new Date());
    }

    return dateToState(date);
}

export const ensureDateStringHasTime = (dateString) => {
    if (!dateString) {
        return null;
    }

    return (dateString.length < 12)
        ? dateString + 'T00:00:00'
        : dateString;
}

export const startOfNextDay = (date) => {
    date = dateFromState(date);
    return startOfMinute(addMinutes(endOfDay(date), 1));
}

export const setDateKeepTime = (oldDateStringWithTime, newDateString) => {
    const newDate = dateFromState(dateForStateFromDateInput(newDateString));
    const oldDateWithTime = dateFromState(oldDateStringWithTime);
    const newDateTime = set(
        oldDateWithTime,
        {
            year: newDate.getFullYear(),
            month: newDate.getMonth(),
            date: newDate.getDate()
        }
    );

    return dateToState(newDateTime);
}


export const getDateRangeString = (startDate, endDate, time) =>
{
    let dateRangeText = ''

    let start = DateTime.fromSQL(`${startDate} ${time}`);
    dateRangeText += start.toFormat(DateFormat.HUMAN_SLASHED_DATE) + ' until ';

    if (endDate) {
        let end = DateTime.fromSQL(`${endDate} ${time}`);
        dateRangeText += end.toFormat(DateFormat.HUMAN_SLASHED_DATE) + ' at ' + start.toFormat(DateFormat.MYSQL_TIME_WITHOUT_SECONDS)
    } else {

    }
    return dateRangeText;
}

export const getRecurrenceDescription = (recurrence, prefix = 'Occurs') => {
    let dateRangeText = getDateRangeString(recurrence.start_date, recurrence.end_date, recurrence.time);
    let periodText = getRecurrencePeriodDescription(recurrence);
    let onText = getRecurrenceOnDescription(recurrence);

    if (onText && onText.length) {
        return `${prefix} ${periodText} ${onText} effective ${dateRangeText}.`;
    } else {
        return `${prefix} ${periodText} effective ${dateRangeText}.`;
    }
}

export const getShortRecurrenceDescription = (recurrence, prefix = 'Occurs') => {
    let periodText = getRecurrencePeriodDescription(recurrence);
    let onText = getRecurrenceOnDescription(recurrence);

    if (onText && onText.length) {
        return `${prefix} ${periodText} ${onText}.`;
    } else {
        return `${prefix} ${periodText}.`;
    }
}

const getRecurrencePeriodDescription = (recurrence) => {
    let period = '';

    switch (recurrence.repeats) {
        case 'daily':
            if (recurrence.every === 1) {
                return 'daily';
            }
            period = 'day';
            break;

        case 'weekly':
            period = 'week';
            break;

        case 'monthly':
            period = 'month';
            break;

        case 'yearly':
            if (recurrence.every === 1) {
                return 'annually';
            }
            period = 'year';
            break;
    }

    if (recurrence.every === 1) {
        return `${period}ly`;
    }
    if (recurrence.every === 2) {
        return `every other ${period}`;
    }
    return `every ${recurrence.every} ${period}s`;
}

// TODO - Implement code to describe when the occurrences occur
const getRecurrenceOnDescription = (recurrence) => {
    if (recurrence.repeats === 'daily') {
        return '';
    }

    if (recurrence.repeats === 'weekly') {

    }

    return '';
}

/**
 * This takes a datetime object and returns it in a format
 * that can be used on an input of type: datetime-local
 *
 * Please somebody tell me there's a better way of getting an appropriately
 * formatted string for this input????
 */
export const getDateTimeFormattedForInput = (dateTime) => {
    return dateTime.toISOString().slice(0,16)
}

export const getUserActionDateString = (time, user) => {
    if (!time) { return ''; }
    return `${formatIsoDateString(time)} (${getUserActionUsername(user)})`
}

export const getUserActionDateStringMultiline = (time, user) => {
    if (!time) { return ''; }
    return (<>
        <div className="user-action-date">{formatIsoDateString(time)}</div>
        <div className="user-action-username">({getUserActionUsername(user)})</div>
    </>);
}

export const getUserActionUsername = (user) => {
    return (user) ? user.fullname : 'System Auto Sign-out'
}

export const formatIsoDateString = (IsoDateString, format = DateTime.DATETIME_SHORT) => {
    if (!IsoDateString || !IsoDateString.length) {
        return '';
    }
    let date = DateTime.fromISO(IsoDateString);
    return date.toLocaleString(format);
}

/**
 * }
 * @param {string|Date} date
 * @param formatString
 * @param currentDate
 * @returns {string}
 */
export const getHumanReadableDate = (
    date,
    formatString = DateFormat.LOCALIZED_DATE_FULL_MONTH,
    currentDate = null
) => {
    if (typeof date == 'string') {
        date = startOfDay(dateFromState(date));
        if (!date) {
            date = startOfDay(dateFromState(date));
        }
    }

    if (currentDate && typeof currentDate == 'string') {
        currentDate = startOfDay(dateFromState(currentDate))
    }

    let today = (currentDate) ? currentDate : startOfDay(new Date());

    let diffDays = differenceInDays(date, today);

    if (diffDays === 0) {
        return 'Today';
    }

    if (diffDays === 1) {
        return 'Tomorrow';
    }

    if (diffDays === -1) {
        return 'Yesterday';
    }

    if (!(date instanceof Date)) {
        return 'DATE FORMAT ERROR';
    }

    return format(date, formatString);
}

export const convertDurationToMinutes = (duration) => {
    let base = parse('00:00', 'HH:mm', new Date());
    let durationDate = parse(duration, 'HH:mm', new Date());

    return differenceInMinutes(durationDate, base);
}

/**
 *
 * @param availableIntervals
 * @param boundary
 * @param minutes
 * @param includeStart
 * @param includeEnd
 * @returns {*[]}
 */
export const getTimeRangeOptions = (availableIntervals, boundary, minutes = 15, includeStart = true, includeEnd = true) => {
    availableIntervals = intervalArrayFromState(availableIntervals);
    boundary = intervalFromState(boundary);

    let options = [];

    // Add an option for every time period within each opening period
    for (let i in availableIntervals) {
        if (!availableIntervals.hasOwnProperty(i)) { continue; }

        var currentDateTime = availableIntervals[i].start;
        var end = subMinutes(availableIntervals[i].end, minutes);

        while (currentDateTime <= end) {

            if (isWithinInterval(currentDateTime, boundary)
                && isWithinInterval(addMinutes(currentDateTime, minutes), boundary)
            ) {
                options.push({
                    label: format(currentDateTime, 'HH:mm'),
                    value: format(currentDateTime, 'yyyy-MM-dd HH:mm:ss'),
                });
            }

            currentDateTime = addMinutes(currentDateTime, minutes);
        }
    }

    if (!includeStart) {
        options = options.slice(1);
    }

    if (includeEnd) {
        options.push({
            label: format(currentDateTime, 'HH:mm'),
            value: format(currentDateTime, 'yyyy-MM-dd HH:mm:ss'),
        });
    }

    return options;
}

export const getDurationOptions = (maxDuration = 1440, selectedInterval, blockedIntervals = []) => {
    selectedInterval = intervalFromState(selectedInterval);
    blockedIntervals = intervalArrayFromState(blockedIntervals);


    let minutesPerDivision = 15;
    if (!maxDuration) {
        maxDuration = 60 * 24;
    }

    let thisTime = (selectedInterval)
        ? {...selectedInterval}
        : {start: new Date(), end: null}

    thisTime.end = addMinutes(thisTime.start, maxDuration);

    blockedIntervals = clampIntervals(blockedIntervals, thisTime);
    let firstBlockedInterval = (blockedIntervals) ? blockedIntervals[0] : null;

    let result = [];
    let cumulativeMinutes = 0;

    while (addMinutes(thisTime.start, cumulativeMinutes) < thisTime.end) {
        cumulativeMinutes += minutesPerDivision;

        // Stop adding durations if we've hit the first blocked interval
        if (firstBlockedInterval &&
            addMinutes(thisTime.start, cumulativeMinutes) > firstBlockedInterval.start
        ) {
            break;
        }

        result.push({
            value: cumulativeMinutes,
            label: getDurationLabel(cumulativeMinutes)
        })
    }

    return result;
}

export const getSimpleDurationOptions = (maxDuration = 1440, minDuration = 0, minutesPerDivision = 15) => {
    let result = [];
    let cumulativeMinutes = 0;

    while (cumulativeMinutes <= maxDuration) {
        if (cumulativeMinutes >= minDuration && cumulativeMinutes <= maxDuration) {
            result.push({
                value: cumulativeMinutes,
                label: getDurationLabel(cumulativeMinutes)
            })
        }
        cumulativeMinutes += minutesPerDivision;
    }

    return result;
}

/**
 * Returns a label decribing a number of minutes in a more human readable
 * format, for use in a duration select box.
 * @param minutes
 * @returns {string}
 */
export const getDurationLabel = (minutes) => {
    if (minutes === 0) {
        return 'None';
    }

    let hours = Math.floor(minutes / 60);
    let mins = minutes % 60;
    let labelChunks = [];
    if (hours) {
        let hrs = (hours > 1) ? 'hrs' : 'hr';
        labelChunks.push(`${hours} ${hrs}`);
    }
    if (mins) {
        labelChunks.push(`${mins} mins`);
    }
    return labelChunks.join(' ');
}

/**
 * Try and discover a date value from the row data based on likely values
 * @param row
 */
export const discoverDateValue = (row) => {
    if (row === null) {
        return null;
    }

    const possibleDateFieldNames = ['date', 'start', 'due_at'];

    for (const fieldName of possibleDateFieldNames) {
        if (row.hasOwnProperty(fieldName)) {
            return row[fieldName];
        }
    }
    return null;
}

export function formatDuration(minutes) {
    if (minutes < 0) {
        return "Invalid input: duration cannot be negative";
    }

    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const remainingMinutes = minutes % 60;

    const parts = [];

    if (days > 0) {
        parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }

    if (hours > 0) {
        parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
    }

    if (remainingMinutes > 0) {
        parts.push(`${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`);
    }

    if (parts.length === 0) {
        return "0 minutes";
    }

    return parts.join(', ');
}
